// Page Tickets pour les vendeurs Pro
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Ticket, MessageCircle, Clock, CheckCircle, Eye, Send, X, ArrowLeft,
  User, Package, Filter, Search, RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/ui/button';

const STATUS_CONFIG = {
  new: { label: 'Nouveau', color: 'bg-blue-100 text-blue-700', icon: '🔵' },
  read: { label: 'Lu', color: 'bg-yellow-100 text-yellow-700', icon: '🟡' },
  replied: { label: 'Répondu', color: 'bg-green-100 text-green-700', icon: '✅' },
  closed: { label: 'Fermé', color: 'bg-gray-100 text-gray-700', icon: '⚫' },
};

const CATEGORY_LABELS = {
  question_produit: { label: 'Question produit', icon: '❓' },
  disponibilite: { label: 'Disponibilité', icon: '📦' },
  livraison: { label: 'Livraison', icon: '🚚' },
  negociation: { label: 'Négociation', icon: '💰' },
  autre: { label: 'Autre', icon: '✉️' },
};

function TicketCard({ ticket, onView, onQuickReply }) {
  const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.new;
  const category = CATEGORY_LABELS[ticket.category] || CATEGORY_LABELS.autre;
  const lastMessage = ticket.messages?.[ticket.messages.length - 1];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl border-2 p-4 hover:shadow-md transition-all cursor-pointer ${
        ticket.status === 'new' ? 'border-blue-300' : 'border-gray-200'
      }`}
      onClick={() => onView(ticket)}
    >
      <div className="flex items-start gap-4">
        {/* Avatar acheteur */}
        <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
          {ticket.buyer_name?.charAt(0) || 'U'}
        </div>

        {/* Contenu */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
              {status.icon} {status.label}
            </span>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              {category.icon} {category.label}
            </span>
          </div>
          
          <h3 className="text-sm font-semibold text-gray-900 truncate">{ticket.subject}</h3>
          <p className="text-xs text-gray-500 mb-2">
            De : {ticket.buyer_name} • {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
          </p>
          
          {lastMessage && (
            <p className="text-sm text-gray-600 line-clamp-2">
              {lastMessage.from === 'seller' ? '↩️ Toi : ' : ''}{lastMessage.message}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onView(ticket);
            }}
          >
            <Eye size={14} className="mr-1" /> Voir
          </Button>
          {ticket.status !== 'replied' && ticket.status !== 'closed' && (
            <Button
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={(e) => {
                e.stopPropagation();
                onQuickReply(ticket);
              }}
            >
              <Send size={14} className="mr-1" /> Répondre
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function TicketsProPage({ onSelectTool }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, replied: 0, closed: 0 });
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const SELLER_ID = 'v1'; // Demo seller ID

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets/seller/${SELLER_ID}`);
      const data = await response.json();
      setTickets(data.tickets || []);
      
      // Fetch stats
      const statsResponse = await fetch(`${API_URL}/api/tickets/seller/${SELLER_ID}/stats`);
      const statsData = await statsResponse.json();
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      // Demo data fallback
      setTickets([
        {
          id: 'demo-1',
          buyer_id: 'b1',
          buyer_name: 'Marie Dupont',
          seller_id: SELLER_ID,
          category: 'question_produit',
          subject: 'Question sur la Perceuse Bosch Pro',
          status: 'new',
          messages: [
            {
              from: 'buyer',
              sender_name: 'Marie Dupont',
              message: 'Bonjour, est-ce que cette perceuse est adaptée pour percer du béton ? Merci !',
              timestamp: new Date().toISOString()
            }
          ],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: 'demo-2',
          buyer_id: 'b2',
          buyer_name: 'Paul Martin',
          seller_id: SELLER_ID,
          category: 'livraison',
          subject: 'Délai de livraison pour Marseille',
          status: 'new',
          messages: [
            {
              from: 'buyer',
              sender_name: 'Paul Martin',
              message: 'Bonjour, je suis sur Marseille, quel est le délai de livraison pour la meuleuse Makita ?',
              timestamp: new Date(Date.now() - 3600000).toISOString()
            }
          ],
          created_at: new Date(Date.now() - 3600000).toISOString(),
          updated_at: new Date(Date.now() - 3600000).toISOString()
        },
        {
          id: 'demo-3',
          buyer_id: 'b3',
          buyer_name: 'Jean Lefebvre',
          seller_id: SELLER_ID,
          category: 'negociation',
          subject: 'Lot de 3 outils - remise possible ?',
          status: 'replied',
          messages: [
            {
              from: 'buyer',
              sender_name: 'Jean Lefebvre',
              message: 'Bonjour, je souhaite acheter la perceuse, la meuleuse et le kit tournevis. Une remise est-elle possible ?',
              timestamp: new Date(Date.now() - 86400000).toISOString()
            },
            {
              from: 'seller',
              sender_name: 'BricoTools Pro',
              message: 'Bonjour Jean ! Oui, pour un lot de 3 outils je peux vous faire -10%. Ça vous convient ?',
              timestamp: new Date(Date.now() - 43200000).toISOString()
            }
          ],
          created_at: new Date(Date.now() - 86400000).toISOString(),
          updated_at: new Date(Date.now() - 43200000).toISOString()
        },
      ]);
      setStats({ total: 3, new: 2, read: 0, replied: 1, closed: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleView = async (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailModal(true);
    
    // Mark as read if new
    if (ticket.status === 'new') {
      try {
        await fetch(`${API_URL}/api/tickets/${ticket.id}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'read' })
        });
        setTickets(prev => prev.map(t => 
          t.id === ticket.id ? { ...t, status: 'read' } : t
        ));
        setStats(prev => ({ ...prev, new: prev.new - 1, read: prev.read + 1 }));
      } catch (error) {
        console.error('Error updating status:', error);
      }
    }
  };

  const handleQuickReply = (ticket) => {
    setSelectedTicket(ticket);
    setReplyMessage('');
    setShowReplyModal(true);
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setReplyLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets/${selectedTicket.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: replyMessage,
          from_seller: true,
          sender_name: 'BricoTools Pro'
        })
      });
      
      if (response.ok) {
        // Update local state
        const newMessage = {
          from: 'seller',
          sender_name: 'BricoTools Pro',
          message: replyMessage,
          timestamp: new Date().toISOString()
        };
        
        setTickets(prev => prev.map(t => 
          t.id === selectedTicket.id 
            ? { 
                ...t, 
                status: 'replied', 
                messages: [...(t.messages || []), newMessage] 
              } 
            : t
        ));
        
        if (selectedTicket.status !== 'replied') {
          setStats(prev => ({
            ...prev,
            [selectedTicket.status]: Math.max(0, prev[selectedTicket.status] - 1),
            replied: prev.replied + 1
          }));
        }
        
        setShowReplyModal(false);
        setShowDetailModal(false);
        setReplyMessage('');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Erreur lors de l\'envoi');
    } finally {
      setReplyLoading(false);
    }
  };

  const filteredTickets = tickets.filter(t => {
    const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
    const matchesSearch = !searchQuery || 
      t.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.buyer_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onSelectTool && (
            <button 
              onClick={() => onSelectTool('dashboard_pro', 'Tableau de bord')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
          )}
          <div>
            <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes Tickets</h1>
            <p className="text-xs sm:text-sm text-gray-500">{stats.total} ticket{stats.total > 1 ? 's' : ''} reçu{stats.total > 1 ? 's' : ''}</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchTickets}
          disabled={loading}
        >
          <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Recherche - Mobile: sous le titre, au-dessus des stats */}
      <div className="flex items-center gap-2 px-2.5 py-1.5 border border-gray-200 rounded-xl bg-white">
        <Search size={14} className="text-gray-400 flex-shrink-0" />
        <input
          type="text"
          placeholder="Rechercher un ticket..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent border-0 outline-none text-xs text-gray-700 placeholder-gray-400"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button
          onClick={() => setFilterStatus(filterStatus === 'new' ? 'all' : 'new')}
          className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
            filterStatus === 'new' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'
          }`}
        >
          <p className="text-2xl font-bold text-blue-600">{stats.new}</p>
          <p className="text-xs text-gray-500">🔵 Nouveaux</p>
        </button>
        <button
          onClick={() => setFilterStatus(filterStatus === 'read' ? 'all' : 'read')}
          className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
            filterStatus === 'read' ? 'border-yellow-500 ring-2 ring-yellow-200' : 'border-gray-200 hover:border-yellow-300'
          }`}
        >
          <p className="text-2xl font-bold text-yellow-600">{stats.read}</p>
          <p className="text-xs text-gray-500">🟡 Lus</p>
        </button>
        <button
          onClick={() => setFilterStatus(filterStatus === 'replied' ? 'all' : 'replied')}
          className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
            filterStatus === 'replied' ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200 hover:border-green-300'
          }`}
        >
          <p className="text-2xl font-bold text-green-600">{stats.replied}</p>
          <p className="text-xs text-gray-500">✅ Répondus</p>
        </button>
        <button
          onClick={() => setFilterStatus('all')}
          className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
            filterStatus === 'all' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-200 hover:border-orange-300'
          }`}
        >
          <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
          <p className="text-xs text-gray-500">📋 Total</p>
        </button>
      </div>

      {/* Liste des tickets */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Ticket size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery || filterStatus !== 'all' ? 'Aucun ticket trouvé' : 'Aucun ticket'}
          </h3>
          <p className="text-gray-500">
            {searchQuery || filterStatus !== 'all' 
              ? 'Modifie tes filtres' 
              : 'Tu n\'as pas encore reçu de tickets'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              onView={handleView}
              onQuickReply={handleQuickReply}
            />
          ))}
        </div>
      )}

      {/* Modal Détail Ticket */}
      {createPortal(
        <AnimatePresence>
          {showDetailModal && selectedTicket && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/60 p-4"
              onClick={() => setShowDetailModal(false)}
            >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-white/70 mb-1">
                      {CATEGORY_LABELS[selectedTicket.category]?.icon} {CATEGORY_LABELS[selectedTicket.category]?.label}
                    </p>
                    <h3 className="text-sm font-semibold">{selectedTicket.subject}</h3>
                    <p className="text-xs text-white/80">De : {selectedTicket.buyer_name}</p>
                  </div>
                  <button 
                    onClick={() => setShowDetailModal(false)}
                    className="p-1 hover:bg-white/20 rounded-lg"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {selectedTicket.messages?.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.from === 'seller' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl p-3 ${
                        msg.from === 'seller'
                          ? 'bg-orange-500 text-white rounded-br-md'
                          : 'bg-white border border-gray-200 rounded-bl-md'
                      }`}
                    >
                      <p className={`text-sm ${msg.from === 'seller' ? 'text-white' : 'text-gray-800'}`}>
                        {msg.message}
                      </p>
                      <p className={`text-xs mt-1 ${msg.from === 'seller' ? 'text-white/70' : 'text-gray-400'}`}>
                        {msg.sender_name} • {new Date(msg.timestamp).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Zone de réponse */}
              <div className="p-4 border-t bg-white flex-shrink-0">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Écrire une réponse..."
                    className="flex-1 p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendReply()}
                  />
                  <Button
                    className="bg-orange-500 hover:bg-orange-600 text-white px-6"
                    onClick={handleSendReply}
                    disabled={!replyMessage.trim() || replyLoading}
                  >
                    {replyLoading ? (
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Send size={18} />
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Modal Réponse rapide */}
      {createPortal(
        <AnimatePresence>
          {showReplyModal && selectedTicket && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/60 p-4"
              onClick={() => setShowReplyModal(false)}
            >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Répondre à {selectedTicket.buyer_name}</h3>
                    <p className="text-xs text-gray-500">{selectedTicket.subject}</p>
                  </div>
                  <button 
                    onClick={() => setShowReplyModal(false)}
                    className="p-1 hover:bg-gray-100 rounded-lg"
                  >
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Message original */}
              <div className="p-4 bg-gray-50 border-b">
                <p className="text-xs text-gray-500 mb-1">Message de {selectedTicket.buyer_name} :</p>
                <p className="text-sm text-gray-700 italic">
                  "{selectedTicket.messages?.[0]?.message}"
                </p>
              </div>

              {/* Réponse */}
              <div className="p-4">
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Ta réponse..."
                  className="w-full p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={4}
                  autoFocus
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 p-4 border-t bg-gray-50">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowReplyModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={handleSendReply}
                  disabled={!replyMessage.trim() || replyLoading}
                >
                  {replyLoading ? 'Envoi...' : 'Envoyer'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
